import {
  normalizePayloadValue,
  UPLOAD_PROGRESS_MAX,
} from 'helpers/publishProgressUtils';
import {
  submitPublishRequest,
  uploadPublishImage,
  uploadPublishImageAsset,
} from 'helpers/publishJobUtils';

export const runBannerPublishJob = async ({
  job,
  files,
  accessToken,
  onProgress,
  signal,
}) => {
  const editing = job?.mode === 'EDIT' && job?.recordId;
  const needsWebUpload = Boolean(files?.webFile);
  const needsMobileUpload = Boolean(files?.mobileFile);

  if (!editing && (!needsWebUpload || !needsMobileUpload)) {
    throw new Error('Both web image and mobile image are required.');
  }

  const webImage = await uploadPublishImage({
    file: files?.webFile,
    existingUrl: files?.existingWebImage || job.payload?.webImage,
    accessToken,
    signal,
    onProgress,
    phase: 'uploading-web-image',
    rangeStart: 0,
    rangeEnd: needsMobileUpload ? 40 : UPLOAD_PROGRESS_MAX,
  });

  const mobileImage = await uploadPublishImage({
    file: files?.mobileFile,
    existingUrl: files?.existingMobileImage || job.payload?.mobileImage,
    accessToken,
    signal,
    onProgress,
    phase: 'uploading-mobile-image',
    rangeStart: needsWebUpload ? 40 : 0,
    rangeEnd: UPLOAD_PROGRESS_MAX,
  });

  if (!webImage || !mobileImage) {
    throw new Error('Both web image and mobile image are required.');
  }

  return submitPublishRequest({
    job,
    createPath: '/banner/create',
    updatePath: (id) => `/banner/update/${id}`,
    payload: {
      ...job.payload,
      webImage,
      mobileImage,
      title: normalizePayloadValue(job.payload?.title ?? job.title),
    },
    accessToken,
    signal,
    onProgress,
    entityLabel: 'banner',
  });
};

export const runTemplatePublishJob = async ({
  job,
  files,
  accessToken,
  onProgress,
  signal,
}) => {
  const payload = { ...job.payload };
  let uploadPercent = 0;

  const fcmImage = await uploadPublishImage({
    file: files?.fcmFile,
    existingUrl: files?.existingFcmImage || payload?.fcm?.image,
    accessToken,
    signal,
    onProgress,
    phase: 'uploading-fcm-image',
    rangeStart: 0,
    rangeEnd: files?.whatsappFile ? 42 : UPLOAD_PROGRESS_MAX,
    thumbnailRequired: 'No',
  });

  if (fcmImage) {
    payload.fcm = {
      ...payload.fcm,
      image: fcmImage,
    };
    uploadPercent = files?.whatsappFile ? 42 : UPLOAD_PROGRESS_MAX;
  }

  const whatsappImage = await uploadPublishImage({
    file: files?.whatsappFile,
    existingUrl: files?.existingWhatsappImage || payload?.whatsapp?.image,
    accessToken,
    signal,
    onProgress,
    phase: 'uploading-whatsapp-image',
    rangeStart: uploadPercent,
    rangeEnd: UPLOAD_PROGRESS_MAX,
    thumbnailRequired: 'No',
  });

  if (whatsappImage) {
    payload.whatsapp = {
      ...payload.whatsapp,
      image: whatsappImage,
    };
    uploadPercent = UPLOAD_PROGRESS_MAX;
  }

  return submitPublishRequest({
    job,
    createPath: '/template/create/admin',
    updatePath: (id) => `/template/update/admin/${id}`,
    payload: {
      ...payload,
      name: normalizePayloadValue(payload.name ?? job.title),
    },
    accessToken,
    signal,
    onProgress,
    entityLabel: 'template',
    uploadPercent: uploadPercent || UPLOAD_PROGRESS_MAX,
  });
};

export const runPagesPublishJob = async ({
  job,
  files,
  accessToken,
  onProgress,
  signal,
}) => {
  const payload = { ...job.payload };
  const imageItems = Array.isArray(files?.imageItems) ? files.imageItems : [];
  const fileItems = Array.isArray(files?.fileItems) ? files.fileItems : [];
  const totalUploads = imageItems.length + fileItems.length;

  if (!imageItems.length) {
    throw new Error('Add atleast one image');
  }

  if (!imageItems.some((item) => item?.mainImage === 'Yes')) {
    throw new Error('Select a main image from images!');
  }

  const uploadedImages = [];
  let uploadCursor = 0;

  for (let i = 0; i < imageItems.length; i += 1) {
    const item = imageItems[i];
    const rangeStart =
      totalUploads > 0
        ? Math.round((uploadCursor / totalUploads) * UPLOAD_PROGRESS_MAX)
        : 0;
    uploadCursor += 1;
    const rangeEnd =
      totalUploads > 0
        ? Math.round((uploadCursor / totalUploads) * UPLOAD_PROGRESS_MAX)
        : UPLOAD_PROGRESS_MAX;

    const uploaded = await uploadPublishImageAsset({
      file: item?.file,
      existingUrl: item?.existingUrl,
      existingThumbnail: item?.existingThumbnail,
      accessToken,
      signal,
      onProgress,
      phase: 'uploading-image',
      rangeStart,
      rangeEnd,
      thumbnailRequired: 'Yes',
    });

    if (!uploaded?.url) {
      throw new Error('Image upload failed');
    }

    if (!uploaded?.thumbnail) {
      throw new Error('Image thumbnail is required');
    }

    uploadedImages.push({
      url: uploaded.url,
      thumbnail: uploaded.thumbnail,
      mainImage: item?.mainImage,
      imageTag: item?.imageTag,
    });
  }

  const uploadedFiles = [];
  for (let i = 0; i < fileItems.length; i += 1) {
    const item = fileItems[i];
    const rangeStart =
      totalUploads > 0
        ? Math.round((uploadCursor / totalUploads) * UPLOAD_PROGRESS_MAX)
        : 0;
    uploadCursor += 1;
    const rangeEnd =
      totalUploads > 0
        ? Math.round((uploadCursor / totalUploads) * UPLOAD_PROGRESS_MAX)
        : UPLOAD_PROGRESS_MAX;

    const uploaded = await uploadPublishImageAsset({
      file: item?.file,
      existingUrl: item?.existingUrl,
      accessToken,
      signal,
      onProgress,
      phase: 'uploading-file',
      rangeStart,
      rangeEnd,
      thumbnailRequired: 'No',
    });

    if (uploaded?.url) {
      uploadedFiles.push(uploaded.url);
    }
  }

  return submitPublishRequest({
    job,
    createPath: '/Pages/create',
    updatePath: (id) => `/Pages/update/${id}`,
    payload: {
      ...payload,
      images: uploadedImages,
      files: uploadedFiles,
      name: normalizePayloadValue(payload.name ?? job.title),
    },
    accessToken,
    signal,
    onProgress,
    entityLabel: 'page',
    uploadPercent: UPLOAD_PROGRESS_MAX,
  });
};

const runSimpleEntityPublishJob = async ({
  job,
  accessToken,
  onProgress,
  signal,
  createPath,
  updatePath,
  entityLabel,
  titleField = 'name',
  updateMethod = 'PUT',
}) => {
  const payload = { ...job.payload };

  return submitPublishRequest({
    job,
    createPath,
    updatePath,
    payload: {
      ...payload,
      ...(titleField && payload[titleField] !== undefined
        ? { [titleField]: normalizePayloadValue(payload[titleField] ?? job.title) }
        : {}),
    },
    accessToken,
    signal,
    onProgress,
    entityLabel,
    uploadPercent: 0,
    updateMethod,
  });
};

export const runCategoriesPublishJob = (params) =>
  runSimpleEntityPublishJob({
    ...params,
    createPath: '/pagesCategory/create',
    updatePath: (id) => `/pagesCategory/update/${id}`,
    entityLabel: 'category',
    titleField: 'name',
  });

export const runWidgetsPublishJob = async ({
  job,
  files,
  accessToken,
  onProgress,
  signal,
}) => {
  const payload = { ...job.payload };
  const imageItems = Array.isArray(files?.imageItems) ? files.imageItems : [];
  const approachItems = Array.isArray(files?.approachItems)
    ? files.approachItems
    : [];
  const totalUploads = imageItems.length + approachItems.length;
  let uploadCursor = 0;
  let uploadPercent = 0;

  const uploadOne = async (item, phase) => {
    const rangeStart =
      totalUploads > 0
        ? Math.round((uploadCursor / totalUploads) * UPLOAD_PROGRESS_MAX)
        : 0;
    uploadCursor += 1;
    const rangeEnd =
      totalUploads > 0
        ? Math.round((uploadCursor / totalUploads) * UPLOAD_PROGRESS_MAX)
        : UPLOAD_PROGRESS_MAX;

    const uploaded = await uploadPublishImageAsset({
      file: item?.file,
      existingUrl: item?.existingUrl,
      existingThumbnail: item?.existingThumbnail,
      accessToken,
      signal,
      onProgress,
      phase,
      rangeStart,
      rangeEnd,
      thumbnailRequired: 'Yes',
    });

    if (!uploaded?.url) {
      throw new Error('Image upload failed');
    }

    return uploaded;
  };

  if (imageItems.length) {
    const uploadedImages = [];
    for (let i = 0; i < imageItems.length; i += 1) {
      const item = imageItems[i];
      const uploaded = await uploadOne(item, 'uploading-image');
      uploadedImages.push({
        url: uploaded.url,
        thumbnail: uploaded.thumbnail || null,
        imageTag: item?.imageTag || null,
      });
    }
    payload.widgetData = {
      ...(payload.widgetData || {}),
      images: uploadedImages,
    };
    uploadPercent = UPLOAD_PROGRESS_MAX;
  }

  if (approachItems.length) {
    const items = [];
    for (let i = 0; i < approachItems.length; i += 1) {
      const item = approachItems[i];
      const uploaded = await uploadOne(item, 'uploading-icon');
      items.push({
        title: item?.title || null,
        description: item?.description || null,
        icon: uploaded.url,
        thumbnail: uploaded.thumbnail || null,
        imageTag: item?.imageTag || null,
      });
    }
    payload.widgetData = {
      ...(payload.widgetData || {}),
      items,
    };
    uploadPercent = UPLOAD_PROGRESS_MAX;
  }

  return submitPublishRequest({
    job,
    createPath: '/widgets/create/admin',
    updatePath: (id) => `/widgets/update/admin/${id}`,
    payload: {
      ...payload,
      title: normalizePayloadValue(payload.title ?? job.title),
    },
    accessToken,
    signal,
    onProgress,
    entityLabel: 'widget',
    uploadPercent: uploadPercent || UPLOAD_PROGRESS_MAX,
  });
};

export const runSeoPublishJob = (params) =>
  runSimpleEntityPublishJob({
    ...params,
    createPath: '/pages-seo/create',
    updatePath: (id) => `/pages-seo/update/${id}`,
    entityLabel: 'SEO entry',
    titleField: 'metatitle',
  });

export const runInformationPolicyPublishJob = (params) =>
  runSimpleEntityPublishJob({
    ...params,
    createPath: '/information-policy/add',
    updatePath: (id) => `/information-policy/update/${id}`,
    entityLabel: 'information policy',
    titleField: null,
    updateMethod: 'PATCH',
  });

export const runConfigSettingsPublishJob = (params) =>
  runSimpleEntityPublishJob({
    ...params,
    createPath: '/config-settings/update/admin',
    updatePath: (id) => `/config-settings/update/admin/${id}`,
    entityLabel: 'config settings',
    titleField: null,
    updateMethod: 'PUT',
  });

export const runTaxCategoryPublishJob = (params) =>
  runSimpleEntityPublishJob({
    ...params,
    createPath: '/tax-category/create/admin',
    updatePath: (id) => `/tax-category/update/admin/${id}`,
    entityLabel: 'tax category',
    titleField: 'name',
    updateMethod: 'PUT',
  });

export const runEmployerPublishJob = (params) =>
  runSimpleEntityPublishJob({
    ...params,
    createPath: '/employer/admin/create',
    updatePath: (id) => `/employer/admin/update/${id}`,
    entityLabel: 'employer',
    titleField: 'companyName',
    updateMethod: 'PATCH',
  });

export const runBranchPublishJob = (params) =>
  runSimpleEntityPublishJob({
    ...params,
    createPath: '/branch/admin/create',
    updatePath: (id) => `/branch/admin/update/${id}`,
    entityLabel: 'branch',
    titleField: 'name',
    updateMethod: 'PATCH',
  });

export const runStaffPublishJob = (params) =>
  runSimpleEntityPublishJob({
    ...params,
    createPath: '/staff/create',
    updatePath: (id) => `/staff/admin/update/${id}`,
    entityLabel: 'staff',
    titleField: 'name',
    updateMethod: 'PATCH',
  });

export const runShiftPublishJob = (params) =>
  runSimpleEntityPublishJob({
    ...params,
    createPath: '/shift/admin/create',
    updatePath: (id) => `/shift/admin/update/${id}`,
    entityLabel: 'shift',
    titleField: 'location',
    updateMethod: 'PATCH',
    payload: (() => {
      const payload = { ...params.job?.payload };
      delete payload.qualification;
      delete payload.experience;
      return payload;
    })(),
  });

export const runTransactionPublishJob = (params) =>
  runSimpleEntityPublishJob({
    ...params,
    createPath: '/transaction/create',
    updatePath: (id) => `/transaction/admin/update/${id}`,
    entityLabel: 'transaction',
    titleField: null,
    updateMethod: 'PUT',
  });

export const runShiftApplicationPublishJob = (params) =>
  runSimpleEntityPublishJob({
    ...params,
    createPath: '/shift-applications/admin/create',
    updatePath: () => '/shift-applications/admin/create',
    entityLabel: 'shift application',
    titleField: null,
    updateMethod: 'POST',
  });

export const runTimesheetPublishJob = (params) =>
  runSimpleEntityPublishJob({
    ...params,
    createPath: '/timesheet/admin/create',
    updatePath: (id) => `/timesheet/admin/${id}`,
    entityLabel: 'timesheet',
    titleField: null,
    updateMethod: 'PATCH',
  });

export const runStaffAvailabilityPublishJob = (params) =>
  runSimpleEntityPublishJob({
    ...params,
    createPath: '/staff-availability/admin/create',
    updatePath: () => '/staff-availability/admin/create',
    entityLabel: 'staff availability',
    titleField: null,
    updateMethod: 'POST',
  });

const STAFF_DOCUMENT_KEYS = [
  'cvResume',
  'passport',
  'nmcRegistrationCertificate',
  'qualificationCertificate',
  'dbsCertificate',
  'shareCode',
  'otherDocument',
  'trainingCertificate',
  'referenceLetter',
  'referenceLetter2',
  'photo',
  'basicLifeSupport',
  'movingAndHandling',
  'fireSafety',
  'firstAid',
  'dementiaCare',
  'healthAndSafety',
  'equalityAndDiversity',
  'infectionPreventionAndControl',
  'mentalCapacityActAndDols',
  'safeguardingAdultLevel2',
  'medicationAwareness',
];

const VACCINATION_DOSE_KEYS = ['firstDose', 'secondDose', 'thirdDose'];

const TRAINING_DOCUMENT_KEYS = new Set([
  'basicLifeSupport',
  'movingAndHandling',
  'fireSafety',
  'firstAid',
  'dementiaCare',
  'healthAndSafety',
  'equalityAndDiversity',
  'infectionPreventionAndControl',
  'mentalCapacityActAndDols',
  'safeguardingAdultLevel2',
  'medicationAwareness',
]);

const formatDateValue = (value) => {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  if (typeof value?.format === 'function') return value.format('YYYY-MM-DD');
  return undefined;
};

export const runStaffDocumentsPublishJob = async ({
  job,
  files,
  accessToken,
  onProgress,
  signal,
}) => {
  const payload = { ...job.payload };
  const staffId = payload.staffId;
  delete payload.staffId;

  const keysToUpload = STAFF_DOCUMENT_KEYS.filter((key) => {
    if (key === 'passport') {
      return Boolean(
        files?.passport?.detailsPage?.file ||
          files?.passport?.detailsPage?.existingUrl ||
          files?.passport?.coverPage?.file ||
          files?.passport?.coverPage?.existingUrl ||
          // legacy single-file shape
          files?.passport?.file ||
          files?.passport?.existingUrl,
      );
    }
    return Boolean(files?.[key]?.file || files?.[key]?.existingUrl);
  });
  const total = Math.max(keysToUpload.length, 1);
  const body = {};

  for (let index = 0; index < keysToUpload.length; index += 1) {
    const key = keysToUpload[index];
    const rangeStart = Math.round((index / total) * UPLOAD_PROGRESS_MAX);
    const rangeEnd = Math.round(((index + 1) / total) * UPLOAD_PROGRESS_MAX);
    const meta = payload[key] || {};

    if (key === 'passport') {
      const mid = Math.round((rangeStart + rangeEnd) / 2);
      const detailsUrl = await uploadPublishImage({
        file:
          files?.passport?.detailsPage?.file || files?.passport?.file,
        existingUrl:
          files?.passport?.detailsPage?.existingUrl ||
          files?.passport?.existingUrl,
        accessToken,
        signal,
        onProgress,
        phase: 'uploading-files',
        rangeStart,
        rangeEnd: mid,
        thumbnailRequired: 'No',
      });
      const coverUrl = await uploadPublishImage({
        file: files?.passport?.coverPage?.file,
        existingUrl: files?.passport?.coverPage?.existingUrl,
        accessToken,
        signal,
        onProgress,
        phase: 'uploading-files',
        rangeStart: mid,
        rangeEnd,
        thumbnailRequired: 'No',
      });
      if (!detailsUrl && !coverUrl) {
        continue;
      }
      const item = {};
      if (detailsUrl) item.detailsPage = detailsUrl;
      if (coverUrl) item.coverPage = coverUrl;
      if (meta.status) item.status = meta.status;
      body.passport = item;
      continue;
    }

    const uploadedUrl = await uploadPublishImage({
      file: files?.[key]?.file,
      existingUrl: files?.[key]?.existingUrl,
      accessToken,
      signal,
      onProgress,
      phase: 'uploading-files',
      rangeStart,
      rangeEnd,
      thumbnailRequired: 'No',
    });
    if (!uploadedUrl) {
      continue;
    }

    const item = { file: uploadedUrl };
    if (meta.status) {
      item.status = meta.status;
    }
    if (key === 'nmcRegistrationCertificate') {
      const expiry = formatDateValue(meta.NMCExpiryDate);
      if (expiry) {
        item.NMCExpiryDate = expiry;
      }
    }
    if (key === 'dbsCertificate' && meta.DBSUpdateServiceNumber) {
      item.DBSUpdateServiceNumber =
        typeof meta.DBSUpdateServiceNumber === 'string'
          ? meta.DBSUpdateServiceNumber.trim()
          : meta.DBSUpdateServiceNumber;
    }
    if (key === 'shareCode') {
      if (meta.shareCode) {
        item.shareCode =
          typeof meta.shareCode === 'string'
            ? meta.shareCode.trim()
            : meta.shareCode;
      }
      const expiry = formatDateValue(meta.expiryDate);
      if (expiry) {
        item.expiryDate = expiry;
      }
    }
    if (key === 'otherDocument') {
      if (meta.type) {
        item.type =
          typeof meta.type === 'string' ? meta.type.trim() : meta.type;
      }
      const expiry = formatDateValue(meta.expiryDate);
      if (expiry) {
        item.expiryDate = expiry;
      }
    }
    if (TRAINING_DOCUMENT_KEYS.has(key)) {
      const expiry = formatDateValue(meta.expiryDate);
      if (expiry) {
        item.expiryDate = expiry;
      }
    }
    body[key] = item;
  }

  const nationalInsuranceMeta = payload.nationalInsurance;
  if (
    nationalInsuranceMeta &&
    (nationalInsuranceMeta.nationalInsuranceNumber ||
      nationalInsuranceMeta.status)
  ) {
    const niItem = {};
    if (nationalInsuranceMeta.nationalInsuranceNumber) {
      niItem.nationalInsuranceNumber =
        typeof nationalInsuranceMeta.nationalInsuranceNumber === 'string'
          ? nationalInsuranceMeta.nationalInsuranceNumber
              .replace(/[\s-]/g, '')
              .toUpperCase()
          : nationalInsuranceMeta.nationalInsuranceNumber;
    }
    if (nationalInsuranceMeta.status) {
      niItem.status = nationalInsuranceMeta.status;
    }
    body.nationalInsurance = niItem;
  }

  // shareCode with meta but no file — skip when only status is set
  if (!body.shareCode && payload.shareCode) {
    const shareMeta = payload.shareCode;
    const shareExpiry = formatDateValue(shareMeta.expiryDate);
    const shareCodeValue =
      typeof shareMeta.shareCode === 'string'
        ? shareMeta.shareCode.trim()
        : shareMeta.shareCode;
    if (shareCodeValue || shareExpiry) {
      const shareItem = {};
      if (shareCodeValue) {
        shareItem.shareCode = shareCodeValue;
      }
      if (shareMeta.status) {
        shareItem.status = shareMeta.status;
      }
      if (shareExpiry) {
        shareItem.expiryDate = shareExpiry;
      }
      body.shareCode = shareItem;
    }
  }

  const vaccinationMeta = payload.vaccinationRecord;
  if (vaccinationMeta && typeof vaccinationMeta === 'object') {
    const doses = {};
    VACCINATION_DOSE_KEYS.forEach((doseKey) => {
      const formatted = formatDateValue(vaccinationMeta[doseKey]);
      if (formatted) {
        doses[doseKey] = formatted;
      }
    });
    if (Object.keys(doses).length) {
      body.vaccinationRecord = doses;
    }
  }

  const additionalMeta = payload.additionalDocuments;
  delete payload.additionalDocuments;
  if (Array.isArray(additionalMeta) && additionalMeta.length) {
    const additionalFileMap = files?.additionalDocuments || {};
    const items = [];
    for (let index = 0; index < additionalMeta.length; index += 1) {
      const meta = additionalMeta[index] || {};
      const fileEntry =
        additionalFileMap[String(index)] || additionalFileMap[index];
      let filesUrl = fileEntry?.existingUrl;
      if (fileEntry?.file) {
        filesUrl = await uploadPublishImage({
          file: fileEntry.file,
          existingUrl: fileEntry.existingUrl,
          accessToken,
          signal,
          onProgress,
          phase: 'uploading-files',
          rangeStart: UPLOAD_PROGRESS_MAX - 1,
          rangeEnd: UPLOAD_PROGRESS_MAX,
          thumbnailRequired: 'No',
        });
      }
      const documentName =
        typeof meta.documentName === 'string'
          ? meta.documentName.trim()
          : meta.documentName;
      const expiry = formatDateValue(meta.expiryDate);
      if (!filesUrl && !documentName && !expiry) {
        continue;
      }
      const item = {};
      if (documentName) {
        item.documentName = documentName;
      }
      if (filesUrl) {
        item.files = filesUrl;
      }
      if (expiry) {
        item.expiryDate = expiry;
      }
      if (meta.status) {
        item.status = meta.status;
      }
      items.push(item);
    }
    if (items.length) {
      body.additionalDocuments = items;
    }
  }

  if (job?.mode !== 'EDIT' && !staffId) {
    throw new Error('staffId is required');
  }

  return submitPublishRequest({
    job,
    createPath: `/staff-documents/admin/upload/${staffId}`,
    updatePath: (id) => `/staff-documents/admin/update/${id}`,
    payload: body,
    accessToken,
    signal,
    onProgress,
    entityLabel: 'staff documents',
    uploadPercent: UPLOAD_PROGRESS_MAX,
    updateMethod: 'PATCH',
  });
};

export const runAdminSettingsPublishJob = async ({
  job,
  files,
  accessToken,
  onProgress,
  signal,
}) => {
  const payload = { ...job.payload };
  const hasFavicon = Boolean(files?.faviconFile || files?.existingFavicon);

  const logo = await uploadPublishImage({
    file: files?.logoFile,
    existingUrl: files?.existingLogo || payload?.logo,
    accessToken,
    signal,
    onProgress,
    phase: 'uploading-logo',
    rangeStart: 0,
    rangeEnd: hasFavicon ? 50 : UPLOAD_PROGRESS_MAX,
    thumbnailRequired: 'No',
  });

  if (logo) {
    payload.logo = logo;
  }

  const favIconImage = await uploadPublishImage({
    file: files?.faviconFile,
    existingUrl: files?.existingFavicon || payload?.favIconImage,
    accessToken,
    signal,
    onProgress,
    phase: 'uploading-favicon',
    rangeStart: hasFavicon ? 50 : UPLOAD_PROGRESS_MAX,
    rangeEnd: UPLOAD_PROGRESS_MAX,
    thumbnailRequired: 'No',
  });

  if (favIconImage) {
    payload.favIconImage = favIconImage;
  }

  return submitPublishRequest({
    job,
    createPath: '/AdminSettings/update/admin',
    updatePath: (id) => `/AdminSettings/update/admin/${id}`,
    payload,
    accessToken,
    signal,
    onProgress,
    entityLabel: 'settings',
    uploadPercent: UPLOAD_PROGRESS_MAX,
    updateMethod: 'PATCH',
  });
};
